import {
  partSerialKey,
  type ProjectGraphEdge,
  type ProjectGraphNode,
  type ProjectGraphResponse,
} from '@avisoma/shared';
import { nodeLabel } from './nodeLabel';

/** One identifying field of a node, lower-cased for matching. */
export type SearchField = { field: string; text: string };

/** Lookups over one graph snapshot, built once and shared by the view, the sidebar, and search. */
export type GraphIndex = {
  nodesById: ReadonlyMap<string, ProjectGraphNode>;
  /** The undirected mention neighbours of each node: the tree walks these, never a structural edge. */
  adjacency: ReadonlyMap<string, ReadonlySet<string>>;
  /** Each node's count of mention edges, at either end. */
  degree: ReadonlyMap<string, number>;
  /** The edge between each linked pair, keyed by `pairKey`: a mention edge first, then the lowest id. */
  edgeOfPair: ReadonlyMap<string, ProjectGraphEdge>;
  /** The edges of every kind that end at each node, in graph order. */
  incoming: ReadonlyMap<string, readonly ProjectGraphEdge[]>;
  /** The edges of every kind that start at each node, in graph order. */
  outgoing: ReadonlyMap<string, readonly ProjectGraphEdge[]>;
  /** The non-empty identifying fields of each node that search reads: its own fields, then its label and id. */
  searchFields: ReadonlyMap<string, readonly SearchField[]>;
};

/** A part's serial without whitespace, as its id keys it; search matches it against the query without whitespace. */
export const SERIAL_KEY_FIELD = 'serialKey';

/** The key of an unordered pair of node ids. */
export const pairKey = (a: string, b: string) => (a < b ? `${a}\n${b}` : `${b}\n${a}`);

/** The fields of a node's own kind. A kind added after this release has none until it gets a case here. */
function kindFieldsOf(node: ProjectGraphNode): [string, string | null | undefined][] {
  switch (node.kind) {
    case 'engine':
      return [
        ['esn', node.esn],
        ['engineType', node.engineType],
      ];
    case 'aircraft':
      return [
        ['registration', node.registration],
        ['msn', node.msn],
        ['aircraftType', node.aircraftType],
      ];
    case 'part':
      return [
        ['partNumber', node.partNumber],
        ['serialNumber', node.serialNumber],
        // The part id ignores whitespace in the serial (DR-4976): search matches it whitespace-free too.
        [SERIAL_KEY_FIELD, partSerialKey(node.serialNumber)],
        ['description', node.description],
      ];
    case 'reference':
      return [
        ['number', node.number],
        ['title', node.title],
        ['authority', node.authority],
      ];
    case 'document':
      return [
        ['title', node.title],
        ['category', node.category],
      ];
    default:
      return [];
  }
}

/** Every field search reads: the kind's own fields, then the label and the id. */
const fieldsOf = (node: ProjectGraphNode): [string, string | null | undefined][] => [
  ...kindFieldsOf(node),
  ['label', nodeLabel(node)],
  ['id', node.id],
];

const EMPTY: readonly ProjectGraphEdge[] = [];

/** Whether `a` stands for its pair over `b`: a mention edge over a structural one, then the lower id. */
const preferred = (a: ProjectGraphEdge, b: ProjectGraphEdge) =>
  (a.kind === 'mentions') !== (b.kind === 'mentions') ? a.kind === 'mentions' : a.id < b.id;

/** Builds every lookup in one pass over the nodes and one over the edges. Prefer `indexOf`, which caches. */
export function buildGraphIndex(graph: ProjectGraphResponse): GraphIndex {
  const nodesById = new Map<string, ProjectGraphNode>();
  const searchFields = new Map<string, SearchField[]>();
  for (const node of graph.nodes) {
    nodesById.set(node.id, node);
    searchFields.set(
      node.id,
      fieldsOf(node).flatMap(([field, value]) => (value ? [{ field, text: value.toLowerCase() }] : [])),
    );
  }
  const adjacency = new Map<string, Set<string>>();
  const degree = new Map<string, number>();
  const edgeOfPair = new Map<string, ProjectGraphEdge>();
  const incoming = new Map<string, ProjectGraphEdge[]>();
  const outgoing = new Map<string, ProjectGraphEdge[]>();
  const link = (from: string, to: string) => {
    const set = adjacency.get(from);
    if (set) set.add(to);
    else adjacency.set(from, new Set([to]));
    degree.set(from, (degree.get(from) ?? 0) + 1);
  };
  const push = (map: Map<string, ProjectGraphEdge[]>, id: string, edge: ProjectGraphEdge) => {
    const list = map.get(id);
    if (list) list.push(edge);
    else map.set(id, [edge]);
  };
  for (const edge of graph.edges) {
    // `instance-of` (part → IPC position) links every part an IPC position has (a `previously-at` edge always shares
    // its pair), so it must be traversable too. `interfaces` / `kit` (IPC position ↔ IPC position, DR-5201) are the
    // only edges between two positions, so they are traversable as well.
    if (edge.kind === 'mentions' || edge.kind === 'instance-of' || edge.kind === 'interfaces' || edge.kind === 'kit') {
      link(edge.from, edge.to);
      link(edge.to, edge.from);
    }
    push(outgoing, edge.from, edge);
    push(incoming, edge.to, edge);
    const key = pairKey(edge.from, edge.to);
    const known = edgeOfPair.get(key);
    if (!known || preferred(edge, known)) edgeOfPair.set(key, edge);
  }
  return { nodesById, adjacency, degree, edgeOfPair, incoming, outgoing, searchFields };
}

const cache = new WeakMap<ProjectGraphResponse, GraphIndex>();

/** The index of a graph snapshot, built on first use and cached by the graph object's identity. */
export function indexOf(graph: ProjectGraphResponse): GraphIndex {
  const known = cache.get(graph);
  if (known) return known;
  const index = buildGraphIndex(graph);
  cache.set(graph, index);
  return index;
}

/** The mention edges that end at a node, in graph order: the documents the sidebar lists under "Mentioned by". */
export const incomingMentions = (index: GraphIndex, id: string): readonly ProjectGraphEdge[] =>
  (index.incoming.get(id) ?? EMPTY).filter((e) => e.kind === 'mentions');

/**
 * Each node's count of incoming mention edges: the documents that mention an entity, the number the sidebar's
 * "Mentioned by N documents" shows. Nodes without one are absent or 0.
 */
export const mentionCounts = (index: GraphIndex): ReadonlyMap<string, number> =>
  new Map([...index.incoming.keys()].map((id) => [id, incomingMentions(index, id).length]));

/** The edges that start or end at a node: incoming first, each in graph order. */
export const edgesOf = (index: GraphIndex, id: string): readonly ProjectGraphEdge[] => [
  ...(index.incoming.get(id) ?? EMPTY),
  ...(index.outgoing.get(id) ?? EMPTY),
];
